import { DATE_FORMATS, DateUtils } from "shared/dateUtils";

export type EntityId = number | string;
export type PeriodValue = number | string | null | undefined;

export interface CyclePeriodRecord {
  AlturaEntrada?: PeriodValue;
  AlturaSaida?: PeriodValue;
  DiaFim?: PeriodValue;
  DiaInicio?: PeriodValue;
  IdPeriodoCicloForragemFazenda?: number | null;
  MesFim?: PeriodValue;
  MesInicio?: PeriodValue;
  QuantidadeDiasDescanso?: PeriodValue;
  Rotulo?: string | null;
}

export interface EditableCyclePeriod extends CyclePeriodRecord {
  Rotulo: string;
  dataFim: string;
  dataInicio: string;
}

export interface ForageCycleRecord {
  IdCicloForragemFazenda: number | null;
  IdFazenda: EntityId;
  IdForragem: EntityId;
  PeriodoCicloForragemFazenda?: CyclePeriodRecord[];
  nomeForragem?: string;
  quantidadePastosAtivos?: number;
}

export interface PastureRecord {
  Excluido?: boolean;
  IdFazenda: EntityId;
  IdForragem: EntityId | null;
}

export interface CyclePeriodPayload {
  AlturaEntrada: number;
  AlturaSaida: number;
  DiaFim: number;
  DiaInicio: number;
  IdPeriodoCicloForragemFazenda?: number;
  MesFim: number;
  MesInicio: number;
  QuantidadeDiasDescanso: number;
  Rotulo: string | null;
}

export interface ForageCyclePayload {
  IdFazenda: number;
  IdForragem: EntityId;
  PeriodoCicloForragemFazenda: CyclePeriodPayload[];
}

export interface CycleValidationError {
  field:
    | "AlturaEntrada"
    | "AlturaSaida"
    | "QuantidadeDiasDescanso"
    | "dataFim"
    | "dataInicio"
    | "periods";
  index: number;
  messageKey: string;
}

interface ParsedDayMonth {
  day: number;
  dayOfYear: number;
  month: number;
}

function twoDigits(value: PeriodValue): string {
  const text = String(value);
  return text.length === 1 ? `0${text}` : text;
}

function getCurrentCycleYear(): number {
  return DateUtils.now().year();
}

function getDaysInCycleYear(year: number): number {
  const firstDay = DateUtils.parse(`01/01/${year}`, DATE_FORMATS["DD/MM/YYYY"]);
  const nextYearFirstDay = DateUtils.parse(
    `01/01/${year + 1}`,
    DATE_FORMATS["DD/MM/YYYY"]
  );
  return nextYearFirstDay.diff(firstDay, "day");
}

export function formatDayMonth(day: PeriodValue, month: PeriodValue): string {
  if (day == null || month == null || day === "" || month === "") {
    return "";
  }
  return `${twoDigits(day)}/${twoDigits(month)}`;
}

export function createEditablePeriod(
  period: CyclePeriodRecord = {}
): EditableCyclePeriod {
  return {
    ...period,
    dataInicio: formatDayMonth(period.DiaInicio, period.MesInicio),
    dataFim: formatDayMonth(period.DiaFim, period.MesFim),
    Rotulo: period.Rotulo ?? "",
  };
}

export function parseDayMonth(
  value: string | null | undefined
): ParsedDayMonth | null {
  const match = /^(\d{2})\/(\d{2})$/.exec(value || "");
  if (!match) {
    return null;
  }

  const [, dayText, monthText] = match;
  const day = Number(dayText);
  const month = Number(monthText);
  const year = getCurrentCycleYear();
  const fullDate = `${monthText}-${dayText}-${year}`;
  const parsed = DateUtils.parse(fullDate, DATE_FORMATS["MM-DD-YYYY"]);

  if (
    !parsed.isValid() ||
    parsed.format(DATE_FORMATS["MM-DD-YYYY"]) !== fullDate
  ) {
    return null;
  }

  const firstDay = DateUtils.parse(`01/01/${year}`, DATE_FORMATS["DD/MM/YYYY"]);
  return { day, month, dayOfYear: parsed.diff(firstDay, "day") };
}

function isPositiveInteger(value: PeriodValue): boolean {
  return (
    value !== "" &&
    value != null &&
    Number.isInteger(Number(value)) &&
    Number(value) > 0
  );
}

function isNumber(value: PeriodValue): boolean {
  return value !== "" && value != null && Number.isFinite(Number(value));
}

function getPeriodDuration(
  period: { end: ParsedDayMonth; start: ParsedDayMonth },
  daysInYear: number
): number {
  return (
    (period.end.dayOfYear - period.start.dayOfYear + daysInYear) % daysInYear +
    1
  );
}

export function validateCyclePeriods(
  periods: EditableCyclePeriod[]
): CycleValidationError[] {
  const errors: CycleValidationError[] = [];
  if (!periods.length) {
    return [
      { index: -1, field: "periods", messageKey: "cycle-period-required" },
    ];
  }

  const parsedPeriods: Array<{
    end: ParsedDayMonth;
    index: number;
    start: ParsedDayMonth;
  }> = [];
  periods.forEach((period, index) => {
    const start = parseDayMonth(period.dataInicio);
    const end = parseDayMonth(period.dataFim);
    if (!start) {
      errors.push({
        index,
        field: "dataInicio",
        messageKey: period.dataInicio
          ? "cycle-date-invalid"
          : "cycle-field-required",
      });
    }
    if (!end) {
      errors.push({
        index,
        field: "dataFim",
        messageKey: period.dataFim
          ? "cycle-date-invalid"
          : "cycle-field-required",
      });
    }
    if (!isNumber(period.AlturaEntrada)) {
      errors.push({
        index,
        field: "AlturaEntrada",
        messageKey: "cycle-field-required",
      });
    } else if (
      isNumber(period.AlturaSaida) &&
      Number(period.AlturaEntrada) <= Number(period.AlturaSaida)
    ) {
      errors.push({
        index,
        field: "AlturaEntrada",
        messageKey: "cycle-entry-height-must-exceed-exit",
      });
    }
    if (!isNumber(period.AlturaSaida)) {
      errors.push({
        index,
        field: "AlturaSaida",
        messageKey: "cycle-field-required",
      });
    }
    if (!isPositiveInteger(period.QuantidadeDiasDescanso)) {
      errors.push({
        index,
        field: "QuantidadeDiasDescanso",
        messageKey: "cycle-rest-days-must-be-positive-integer",
      });
    }
    if (start && end) parsedPeriods.push({ index, start, end });
  });

  if (parsedPeriods.length === periods.length) {
    const ordered = [...parsedPeriods].sort(
      (left, right) => left.start.dayOfYear - right.start.dayOfYear
    );
    const daysInYear = getDaysInCycleYear(getCurrentCycleYear());

    if (ordered.length === 1) {
      if (getPeriodDuration(ordered[0], daysInYear) !== daysInYear) {
        errors.push({
          index: ordered[0].index,
          field: "dataFim",
          messageKey: "cycle-must-cover-full-year",
        });
      }
    } else {
      ordered.forEach((period, position) => {
        const next = ordered[(position + 1) % ordered.length];
        const expectedNextStart = (period.end.dayOfYear + 1) % daysInYear;
        if (next.start.dayOfYear !== expectedNextStart) {
          const distanceToNextStart =
            (next.start.dayOfYear - period.start.dayOfYear + daysInYear) %
            daysInYear;
          const overlaps =
            distanceToNextStart < getPeriodDuration(period, daysInYear);
          errors.push({
            index: next.index,
            field: "dataInicio",
            messageKey: overlaps
              ? "cycle-periods-overlap"
              : "cycle-periods-have-gap",
          });
        }
      });
    }
  }

  return errors;
}

export function toCyclePayloadPeriods(
  periods: EditableCyclePeriod[]
): CyclePeriodPayload[] {
  return periods.map((period) => {
    const start = parseDayMonth(period.dataInicio);
    const end = parseDayMonth(period.dataFim);
    if (!start || !end) {
      throw new Error("Período de ciclo contém uma data inválida.");
    }
    return {
      ...(period.IdPeriodoCicloForragemFazenda != null
        ? { IdPeriodoCicloForragemFazenda: period.IdPeriodoCicloForragemFazenda }
        : {}),
      DiaInicio: start.day,
      MesInicio: start.month,
      DiaFim: end.day,
      MesFim: end.month,
      AlturaEntrada: Number(period.AlturaEntrada),
      AlturaSaida: Number(period.AlturaSaida),
      QuantidadeDiasDescanso: Number(period.QuantidadeDiasDescanso),
      Rotulo: period.Rotulo ? period.Rotulo : null,
    };
  });
}

export function listCyclesWithActivePastureCounts(
  cycles: ForageCycleRecord[],
  pastures: PastureRecord[],
  farmId: EntityId
): ForageCycleRecord[] {
  const selectedFarmId = Number(farmId);
  const activePastures = pastures.filter(
    (pasture) =>
      Number(pasture.IdFazenda) === selectedFarmId &&
      pasture.Excluido !== true &&
      pasture.IdForragem != null
  );

  const pastureCounts = activePastures.reduce((counts, pasture) => {
    const idForragem = Number(pasture.IdForragem);
    counts.set(idForragem, (counts.get(idForragem) || 0) + 1);
    return counts;
  }, new Map<number, number>());
  const cyclesByForage = new Map<number, ForageCycleRecord>(
    cycles
      .filter((cycle) => Number(cycle.IdFazenda) === selectedFarmId)
      .map((cycle) => [Number(cycle.IdForragem), cycle])
  );

  return Array.from(pastureCounts, ([idForragem, quantidadePastosAtivos]) => ({
    ...(cyclesByForage.get(idForragem) || {
      IdCicloForragemFazenda: null,
      IdFazenda: selectedFarmId,
      IdForragem: idForragem,
      PeriodoCicloForragemFazenda: [],
    }),
    quantidadePastosAtivos,
  }));
}
